import { memo } from 'react'
import { useI18n } from 'builder/hooks/useI18n'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { SectionData } from 'builder/components/Section'
import { translationKeyAliases } from '../SectionsList/constants'
import { Container, Title, List, Item, Description, Header } from './styles'

type Props = {
  sections: SectionData[]
  onAdd: (name: (typeof sectionTypes)[number]) => void
  onAddCustom: (id: string) => void
  customTitle?: string
  customDescription?: string
  isStepByStepFlow?: boolean
}

const sectionTypes = [
  'courses',
  'activities',
  'internships',
  'hobbies',
  'languages',
  'references',
] as const

const getRandomId = () => Math.floor(Math.random() * 0xffffffff).toString()
const isEnabled = (name: string, sections: SectionData[]) =>
  !!sections.find(section => section.id === name)?.hidden
const isPresent = (name: string, sections: SectionData[]) =>
  !!sections.find(section => section.id === name)

export const AdditionalSections = memo(
  ({ sections, onAdd, onAddCustom, customTitle, customDescription, isStepByStepFlow }: Props) => {
    const { i18n } = useI18n()
    const isAtsTemplate = useTypedSelector(
      state =>
        !!state.resumeEditor.resume?.template &&
        ['athens', 'prague'].includes(state.resumeEditor.resume.template),
    )

    return (
      <Container isStepByStepFlow={isStepByStepFlow}>
        <Header isStepByStepFlow={isStepByStepFlow}>
          <Title>{customTitle || i18n.t('builder.resume_editor.add_section')}</Title>
          {customDescription && <Description>{customDescription}</Description>}
        </Header>

        <List>
          <Item type="custom" onClick={() => onAddCustom(getRandomId())}>
            {i18n.t('builder.resume_editor.custom_section.title')}
          </Item>

          {sectionTypes
            .filter(type => isPresent(type, sections))
            .map(type => {
              const section = sections.find(item => item.id === type)
              return (
                <Item
                  key={type}
                  type={type}
                  disabled={!isEnabled(type, sections)}
                  onClick={() => isEnabled(type, sections) && onAdd(type)}
                >
                  {section?.title
                    ? section.title
                    : isAtsTemplate && translationKeyAliases[type]
                    ? i18n.t(translationKeyAliases[type])
                    : i18n.t(`builder.resume_editor.${type}`)}
                </Item>
              )
            })}
        </List>
      </Container>
    )
  },
)
